import React, { useRef, useEffect, useCallback, useMemo } from 'react'

import {
  Button, Checkbox, Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, bool, func, shape, string,
} from 'prop-types'

import Columns from 'Pods/Shared/Icons/Columns'

import useToggle from 'Utils/useToggle'

const POPOVER_ID = 'column-visibility-menu-popover'

function ColumnVisibilityMenu({
  columns,
  visibleKeys,
  onChange,
}) {
  const { isOpen, toggle, close } = useToggle()
  const rootRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return undefined

    const controller = new AbortController()
    const { signal } = controller

    document.addEventListener('mousedown', (event) => {
      if (!rootRef.current?.contains(event.target)) close()
    }, { signal })

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close()
    }, { signal })

    return () => controller.abort()
  }, [isOpen, close])

  const requiredKeys = useMemo(
    () => new Set(columns.filter((col) => col.required).map((col) => col.key)),
    [columns]
  )

  const handleToggleColumn = useCallback((key) => {
    if (requiredKeys.has(key)) return
    const nextKeys = visibleKeys.includes(key)
      ? visibleKeys.filter((k) => k !== key)
      : [...visibleKeys, key]
    onChange(nextKeys)
  }, [requiredKeys, visibleKeys, onChange])

  return (
    <div className="column-visibility-menu" ref={rootRef}>
      <Button
        type="secondary"
        size="small"
        customClass="column-visibility-menu__trigger"
        onClick={toggle}
        icon={<Icon svgComponent={Columns} />}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={POPOVER_ID}
      >
        Columns
      </Button>
      {isOpen && (
        <div id={POPOVER_ID} className="column-visibility-menu__popover">
          <div className="column-visibility-menu__header cgt-body-base">
            Column settings
          </div>
          <ul className="column-visibility-menu__list">
            {columns.map((column) => {
              const isChecked = visibleKeys.includes(column.key)
              const isDisabled = Boolean(column.required)
              return (
                <li key={column.key} className="column-visibility-menu__item">
                  <Checkbox
                    id={`column-visibility-${column.key}`}
                    checkedColor="primary"
                    label={column.label}
                    isChecked={isChecked || isDisabled}
                    isDisabled={isDisabled}
                    onChange={() => handleToggleColumn(column.key)}
                    isControlled
                  />
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

ColumnVisibilityMenu.propTypes = {
  columns: arrayOf(shape({
    key: string.isRequired,
    label: string.isRequired,
    required: bool,
  })).isRequired,
  visibleKeys: arrayOf(string).isRequired,
  onChange: func.isRequired,
}

export default ColumnVisibilityMenu
